import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../ui/tabs';
import {
  Folder, FolderOpen, FilePdf, Image as ImageIcon, MagnifyingGlass,
  DownloadSimple, Trash, Upload, Archive, ClipboardText,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso.slice(0, 10); }
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function FileRow({ file, onDelete }) {
  const isImage = (file.content_type || '').startsWith('image/');
  const Icon = isImage ? ImageIcon : FilePdf;
  const colorCls = isImage ? 'text-blue-500' : 'text-red-500';
  return (
    <div className="flex items-center gap-3 p-2.5 hover:bg-gray-50 rounded-lg group"
      data-testid={`dossier-file-${file.id}`}>
      <Icon size={20} className={colorCls} weight="fill" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#111827] truncate">{file.original_filename}</p>
        <p className="text-xs text-gray-500">
          {formatDate(file.created_at)}{file.size ? ` - ${formatSize(file.size)}` : ''}
          {file.version && file.auto_archived ? ` - V${file.version}` : ''}
        </p>
      </div>
      <a
        href={`${API}/files/${file.id}/download`}
        target="_blank" rel="noopener noreferrer"
        className="p-1.5 hover:bg-gray-200 rounded text-gray-600"
        data-testid={`dossier-download-${file.id}`}
        title="Telecharger">
        <DownloadSimple size={16} />
      </a>
      <button onClick={() => onDelete(file.id)}
        className="p-1.5 hover:bg-red-50 rounded text-red-500 opacity-0 group-hover:opacity-100 transition"
        data-testid={`dossier-delete-${file.id}`}
        title="Supprimer">
        <Trash size={16} />
      </button>
    </div>
  );
}

function VersionGroup({ group, onDelete }) {
  const [open, setOpen] = useState(false);
  const latest = group.versions[0];
  return (
    <div className="border border-gray-100 rounded-lg" data-testid={`dossier-group-${group.document_type}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
        data-testid={`dossier-group-toggle-${group.document_type}`}>
        {open ? <FolderOpen size={20} className="text-[#FF6B00]" weight="fill" />
              : <Folder size={20} className="text-[#FF6B00]" weight="fill" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#111827]">{group.document_label}</p>
          <p className="text-xs text-gray-500">
            {group.count} version{group.count > 1 ? 's' : ''} - derniere : V{group.latest_version} le {formatDate(latest?.created_at)}
          </p>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">
          V{group.latest_version}
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-2 py-1">
          {group.versions.map(v => (
            <FileRow key={v.id} file={v} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-8 text-center text-sm text-gray-400 italic">
      {label}
    </div>
  );
}

/**
 * DossierSheet - dossier documentaire centralise par chantier.
 */
export function DossierSheet({ open, onOpenChange, chantier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Debounce search to avoid flickering during typing
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!chantier?.id) return;
    setLoading(true);
    try {
      const params = debouncedSearch ? `?q=${encodeURIComponent(debouncedSearch)}` : '';
      const res = await axios.get(`${API}/chantiers/${chantier.id}/dossier${params}`,
        { withCredentials: true });
      setData(res.data);
    } catch (_err) {
      toast.error('Erreur chargement du dossier');
    } finally {
      setLoading(false);
    }
  }, [chantier?.id, debouncedSearch]);

  useEffect(() => { if (open && chantier?.id) fetchData(); }, [open, chantier?.id, fetchData]);

  const handleDelete = async (fileId) => {
    try {
      await axios.delete(`${API}/dossier/files/${fileId}`, { withCredentials: true });
      toast.success('Fichier supprime');
      fetchData();
    } catch (_err) { toast.error('Erreur suppression'); }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleUploadSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'manuel');
      await axios.post(`${API}/chantiers/${chantier.id}/dossier/upload`, fd, {
        withCredentials: true,
      });
      toast.success('Document ajoute au dossier');
      await fetchData();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Upload error', err);
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const opcCount = data ? (data.documents_opc.groups.length + data.documents_opc.other.length) : 0;
  const cspsCount = data ? (data.documents_csps.groups.length + data.documents_csps.other.length) : 0;
  const photosCount = data ? data.photos.length : 0;
  const archivesCount = data ? data.archives.length : 0;
  const reservesCount = data ? data.reserves.length : 0;
  const reservesEntityCount = data ? (data.reserves_entity_count || 0) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="dossier-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2" data-testid="dossier-sheet-title">
            <FolderOpen size={22} className="text-[#FF6B00]" weight="fill" />
            Dossier : {chantier?.name || '...'}
          </SheetTitle>
          <SheetDescription>
            Tous les documents du chantier - rapports, PGC/PPSPS/DIUO, plannings, journaux, photos.
          </SheetDescription>
        </SheetHeader>

        {/* Search + Upload */}
        <div className="flex gap-2 mt-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher dans le dossier..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-11"
              data-testid="dossier-search"
            />
          </div>
          <Button onClick={handleUploadClick} variant="outline" disabled={uploading}
            data-testid="dossier-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
            Ajouter
          </Button>
          <input ref={fileInputRef} type="file" className="hidden"
            onChange={handleUploadSelect}
            data-testid="dossier-upload-input" />
        </div>

        {loading && !data ? (
          <div className="py-16 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#FF6B00] mx-auto" />
          </div>
        ) : data && (
          <Tabs defaultValue="opc" className="mt-6">
            <TabsList className="w-full grid grid-cols-5 h-auto">
              <TabsTrigger value="opc" className="text-xs flex flex-col py-2" data-testid="tab-opc">
                <Folder size={16} className="mb-0.5" />
                <span>OPC ({opcCount})</span>
              </TabsTrigger>
              <TabsTrigger value="csps" className="text-xs flex flex-col py-2" data-testid="tab-csps">
                <Folder size={16} className="mb-0.5" />
                <span>CSPS ({cspsCount})</span>
              </TabsTrigger>
              <TabsTrigger value="reserves" className="text-xs flex flex-col py-2" data-testid="tab-reserves">
                <ClipboardText size={16} className="mb-0.5" />
                <span>Reserves ({reservesEntityCount + reservesCount})</span>
              </TabsTrigger>
              <TabsTrigger value="photos" className="text-xs flex flex-col py-2" data-testid="tab-photos">
                <ImageIcon size={16} className="mb-0.5" />
                <span>Photos ({photosCount})</span>
              </TabsTrigger>
              <TabsTrigger value="archives" className="text-xs flex flex-col py-2" data-testid="tab-archives">
                <Archive size={16} className="mb-0.5" />
                <span>Archives ({archivesCount})</span>
              </TabsTrigger>
            </TabsList>

            {/* Documents OPC */}
            <TabsContent value="opc" className="mt-4 space-y-2">
              {data.documents_opc.groups.length === 0 && data.documents_opc.other.length === 0 && (
                <EmptyState label="Aucun document OPC. Les rapports / plannings generes seront archives ici." />
              )}
              {data.documents_opc.groups.map(g => (
                <VersionGroup key={g.document_type} group={g} onDelete={handleDelete} />
              ))}
              {data.documents_opc.other.map(f => (
                <FileRow key={f.id} file={f} onDelete={handleDelete} />
              ))}
            </TabsContent>

            {/* Documents CSPS */}
            <TabsContent value="csps" className="mt-4 space-y-2">
              {data.documents_csps.groups.length === 0 && data.documents_csps.other.length === 0 && (
                <EmptyState label="Aucun document CSPS. Les PGC, PPSPS, DIUO et registres journaux generes seront archives ici." />
              )}
              {data.documents_csps.groups.map(g => (
                <VersionGroup key={g.document_type} group={g} onDelete={handleDelete} />
              ))}
              {data.documents_csps.other.map(f => (
                <FileRow key={f.id} file={f} onDelete={handleDelete} />
              ))}
            </TabsContent>

            {/* Reserves */}
            <TabsContent value="reserves" className="mt-4 space-y-2">
              {reservesEntityCount > 0 && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm"
                  data-testid="dossier-reserves-summary">
                  <p className="font-semibold text-emerald-800 mb-0.5">
                    {reservesEntityCount} reserve{reservesEntityCount > 1 ? 's' : ''} sur ce chantier
                  </p>
                  <a href="/reserves" className="text-xs text-emerald-700 underline">
                    Voir la liste detaillee dans le module Reserves
                  </a>
                </div>
              )}
              {data.reserves.length === 0 && reservesEntityCount === 0 && (
                <EmptyState label="Aucune reserve attachee a ce chantier." />
              )}
              {data.reserves.map(f => (
                <FileRow key={f.id} file={f} onDelete={handleDelete} />
              ))}
            </TabsContent>

            {/* Photos */}
            <TabsContent value="photos" className="mt-4">
              {data.photos.length === 0 ? (
                <EmptyState label="Aucune photo. Les photos uploadees sur les reserves et inspections apparaitront ici." />
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {data.photos.map(p => (
                    <div key={p.id} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
                      data-testid={`dossier-photo-${p.id}`}>
                      <img
                        src={`${API}/files/${p.id}/download`}
                        alt={p.original_filename}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <a href={`${API}/files/${p.id}/download`} target="_blank" rel="noopener noreferrer"
                          className="p-2 bg-white/90 rounded-full text-gray-700">
                          <DownloadSimple size={14} />
                        </a>
                        <button onClick={() => handleDelete(p.id)}
                          className="p-2 bg-white/90 rounded-full text-red-500">
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Archives */}
            <TabsContent value="archives" className="mt-4 space-y-2">
              {data.archives.length === 0 ? (
                <EmptyState label="Aucune archive." />
              ) : (
                data.archives.map(f => <FileRow key={f.id} file={f} onDelete={handleDelete} />)
              )}
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default DossierSheet;
